import React, { useEffect, useState } from 'react'
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import GenericModal from '../../components/GenericModal'
import DefaultModalActions from '../../components/GenericModal/DefaultModalActions'
import { DistributionManager } from '../../APIManager'
import { notification } from '../../components/Notifications'
import { type ApiError } from '../../models/Common'
import { isInstructorReleaseOnGradePostingEnabled } from '../../consts/messages'
import { grey } from '../../colors'

export interface GradePostingInstructorReleaseModalProps {
  distributionSeriesId: string
  seriesTitle?: string
  canEdit: boolean
  isOpen: boolean
  handleCloseModal: () => void
}

const seriesProjectKey = (id: string): unknown[] => ['distributionSeriesProject', id]

const GradePostingInstructorReleaseModal: React.FC<GradePostingInstructorReleaseModalProps> = ({
  distributionSeriesId,
  seriesTitle,
  canEdit,
  isOpen,
  handleCloseModal
}) => {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(false)

  const { data: seriesData, isLoading, isFetching } = useQuery({
    queryKey: seriesProjectKey(distributionSeriesId),
    queryFn: async () => {
      const response = await DistributionManager.getProjectById(distributionSeriesId)
      return response.data?.data ?? response.data
    },
    enabled: isOpen && distributionSeriesId !== ''
  })

  useEffect(() => {
    if (!isOpen) return
    setEnabled(isInstructorReleaseOnGradePostingEnabled(seriesData?.isInstructorReleaseOnGradePostingEnabled))
  }, [isOpen, seriesData?.isInstructorReleaseOnGradePostingEnabled])

  const { mutate: saveSetting, isPending: isSaving } = useMutation({
    mutationFn: async (nextEnabled: boolean) => {
      return await DistributionManager.updateProject(distributionSeriesId, {
        isInstructorReleaseOnGradePostingEnabled: nextEnabled
      })
    },
    onSuccess: async () => {
      notification('Grade-posting instructor release setting saved.', { variant: 'success' })
      await queryClient.invalidateQueries({ queryKey: seriesProjectKey(distributionSeriesId) })
      // The flag is denormalized onto slot GET; refresh slot-level queries so the section
      // table/form pick up the change without requiring a full page reload.
      await queryClient.invalidateQueries({ queryKey: ['slotDataById'] })
      handleCloseModal()
    },
    onError: (error: ApiError) => {
      notification(error?.message ?? 'Failed to save setting. Please try again.', { variant: 'error' })
    },
    meta: { silent: true }
  })

  const handleSave = (): void => {
    if (!canEdit) return
    saveSetting(enabled)
  }

  const loading = isLoading || isFetching

  return (
    <GenericModal
      isOpen={isOpen}
      title="Release Instructor Results on Grade Posting"
      onClose={handleCloseModal}
      maxWidth="sm"
      action={false}
    >
      <Box display="flex" flexDirection="column" gap={3}>
        {seriesTitle && (
          <Typography variant="body2" color={grey[700]}>
            Survey Project: <strong>{seriesTitle}</strong>
          </Typography>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(_event, checked) => { setEnabled(checked) }}
              disabled={!canEdit || loading || isSaving}
            />
          }
          label="Release instructor results on grade posting"
        />

        <Alert severity="info">
          <Typography variant="body2" mb={1}>
            When enabled, instructors see grade-posting messaging instead of a scheduled release date.
            Admins set section instructor release dates manually after grades are posted.
          </Typography>
          <Typography variant="body2" mb={1}>
            Results Release Date in the Survey Project modal is unchanged — it controls aggregate/admin
            release only. It does not set instructor release on auto-synced sections and does not
            automatically release results to instructors.
          </Typography>
          <Typography variant="body2">
            Instructors may never receive results if section instructor release is left unset.
          </Typography>
        </Alert>

        <DefaultModalActions
          confirmButtonProps={{
            text: 'Save',
            onClick: handleSave,
            loading: isSaving,
            disabled: !canEdit || loading
          }}
          destructiveButtonProps={{
            onClick: handleCloseModal,
            disabled: isSaving
          }}
        />
      </Box>
    </GenericModal>
  )
}

export default GradePostingInstructorReleaseModal
